import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Wallet, Loader2, Save } from "lucide-react";

const ALL_NETWORKS = ["visa", "mastercard", "rupay", "amex", "diners", "discover"];
const ALL_TYPES = ["credit", "debit", "prepaid"];

export default function AdminCustomerWalletRules() {
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/customer-wallet/rules")
      .then((r) => setRules(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Failed to load"));
  }, []);

  const toggle = (arrKey, value) => {
    const cur = rules[arrKey] || [];
    const next = cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value];
    setRules({ ...rules, [arrKey]: next });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/customer-wallet/rules", {
        kyc_unverified_max_balance: rules.kyc_unverified_max_balance,
        min_topup: rules.min_topup,
        max_topup: rules.max_topup,
        allowed_card_networks: rules.allowed_card_networks,
        allowed_card_types: rules.allowed_card_types,
        require_payment_method_whitelist: rules.require_payment_method_whitelist,
      });
      setRules(data);
      toast.success("Rules saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!rules) return <DashboardLayout><div className="p-12 text-center"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div>
        <span className="overline">Compliance · Customer Wallet</span>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2 flex items-center gap-2" data-testid="page-title">
          <Wallet className="h-7 w-7 text-primary" strokeWidth={1.5} /> Customer wallet rules
        </h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          Define top-up limits, KYC-less balance cap, and which payment networks/types partners can whitelist for customers.
        </p>
      </div>

      <div className="mt-6 surface-card p-6 space-y-6 max-w-3xl" data-testid="rules-form">
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <Label>KYC-less balance cap (₹)</Label>
            <Input type="number" value={rules.kyc_unverified_max_balance} onChange={(e) => setRules({ ...rules, kyc_unverified_max_balance: parseFloat(e.target.value || "0") })} className="font-mono mt-1" data-testid="rule-kyc-cap" />
            <p className="text-[11px] text-muted-foreground mt-1">Customers without verified KYC cannot hold more than this balance.</p>
          </div>
          <div>
            <Label>Min top-up (₹)</Label>
            <Input type="number" value={rules.min_topup} onChange={(e) => setRules({ ...rules, min_topup: parseFloat(e.target.value || "0") })} className="font-mono mt-1" data-testid="rule-min-topup" />
          </div>
          <div>
            <Label>Max top-up (₹)</Label>
            <Input type="number" value={rules.max_topup} onChange={(e) => setRules({ ...rules, max_topup: parseFloat(e.target.value || "0") })} className="font-mono mt-1" data-testid="rule-max-topup" />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Switch checked={!!rules.require_payment_method_whitelist} onCheckedChange={(v) => setRules({ ...rules, require_payment_method_whitelist: v })} data-testid="rule-require-whitelist" />
          <Label className="cursor-pointer">Enforce payment method whitelist on every top-up</Label>
        </div>

        <div>
          <Label>Allowed card networks</Label>
          <div className="flex flex-wrap gap-2 mt-2" data-testid="networks-row">
            {ALL_NETWORKS.map((n) => {
              const on = (rules.allowed_card_networks || []).includes(n);
              return (
                <button key={n} type="button" onClick={() => toggle("allowed_card_networks", n)} data-testid={`net-${n}`}
                  className={`px-3 py-1 rounded-full text-xs border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "bg-muted/40 text-muted-foreground border-border"}`}>
                  {n.toUpperCase()}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <Label>Allowed card types</Label>
          <div className="flex flex-wrap gap-2 mt-2" data-testid="types-row">
            {ALL_TYPES.map((t) => {
              const on = (rules.allowed_card_types || []).includes(t);
              return (
                <button key={t} type="button" onClick={() => toggle("allowed_card_types", t)} data-testid={`type-${t}`}
                  className={`px-3 py-1 rounded-full text-xs border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "bg-muted/40 text-muted-foreground border-border"}`}>
                  {t}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-2 justify-end">
          {rules.updated_at && <Badge variant="outline" className="text-[10px]">Last updated: {new Date(rules.updated_at).toLocaleString()}</Badge>}
          <Button onClick={save} disabled={saving} data-testid="save-rules">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save rules
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
